import React, { useCallback, useEffect, useState } from 'react';
import useScroll from '../../hooks/useScroll.ts';
import {
  AnswerApplicationQuestionRequest,
  Question,
  SectionIntro,
  SectionOutro,
} from '../../openapi/atlantis';
import { ContentItem } from '../../openapi/yenta/api.ts';
import ErrorService from '../../services/ErrorService.ts';
import { showErrorToast } from '../../slices/ToastNotificationSlice';
import { useAppDispatch } from '../../slices/store';
import {
  ApplicationStepWithNewContent,
  NextActionType,
} from '../../utils/ConfigDrivenFormUtils.tsx';
import Logger from '../../utils/Logger';
import {
  isQuestion,
  isSectionIntro,
  isSectionOutro,
} from '../../utils/TypeUtils';
import DefaultSplashV2 from '../DefaultSplashV2.tsx';
import TCOnboardingConfigDrivenIntroWrapper from './Containers/TCOnboarding/ConfigDrivenIntroWrapper';
import TCOnboardingConfigDrivenOutroWrapper from './Containers/TCOnboarding/ConfigDrivenOutroWrapper';
import ConfigDrivenQuestionWrapper from './Containers/TCOnboarding/ConfigDrivenQuestionWrapper';

interface ConfigDrivenContainerProps {
  onStart: (stepId: string) => void;
  onNext: (actionType: NextActionType) => Promise<ContentItem>;
  onSubmit: (formData: AnswerApplicationQuestionRequest) => Promise<void>;
  onNavigateToQuestion: (questionId: string) => Promise<ContentItem>;
}

const ConfigDrivenContainer: React.FC<ConfigDrivenContainerProps> = ({
  onStart,
  onNext,
  onSubmit,
  onNavigateToQuestion,
}) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [config, setConfig] = useState<ContentItem>();
  const { scrollToTop } = useScroll();

  const handleOnNext = async (
    actionType: NextActionType = NextActionType.BUTTON_ACTION,
  ) => {
    try {
      const data = await onNext(actionType);
      setConfig(data);
      scrollToTop();
    } catch (e: any) {
      Logger.error('Failed to fetch next question:', e);
      ErrorService.notify('Failed to fetch next question', e);
      dispatch(
        showErrorToast(
          'Failed to fetch next question. Please try again in sometime',
        ),
      );
    }
  };

  const handleNavigateToQuestion = async (questionId: string) => {
    try {
      const data = await onNavigateToQuestion(questionId);
      setConfig(data);
    } catch (e: any) {
      Logger.error('Failed to fetch question by id:', e);
      ErrorService.notify('Failed to fetch question by id', e);
      dispatch(
        showErrorToast(
          'Failed to fetch question. Please try again in sometime',
        ),
      );
    }
  };

  const fetchQuestion = useCallback(async () => {
    setIsLoading(true);
    await handleOnNext(NextActionType.INITIATE);
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchQuestion();
  }, [fetchQuestion]);

  if (isLoading) {
    return (
      <div className='h-screen w-screen'>
        <DefaultSplashV2 />
      </div>
    );
  }

  if (isSectionIntro(config!)) {
    return (
      <TCOnboardingConfigDrivenIntroWrapper
        config={config as ApplicationStepWithNewContent<SectionIntro>}
        onNext={handleOnNext}
        onStart={onStart}
      />
    );
  }

  if (isQuestion(config!)) {
    return (
      <ConfigDrivenQuestionWrapper
        config={config as ApplicationStepWithNewContent<Question>}
        onNext={handleOnNext}
        onSubmit={onSubmit}
        onNavigateToQuestion={handleNavigateToQuestion}
      />
    );
  }

  if (isSectionOutro(config!)) {
    return (
      <TCOnboardingConfigDrivenOutroWrapper
        config={config as ApplicationStepWithNewContent<SectionOutro>}
      />
    );
  }

  return undefined;
};

export default ConfigDrivenContainer;
